import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getOperatingMode } from 'src/Services/config.services';
import Cookies from 'js-cookie';

const OperatingModeContext = createContext();

export const OperatingModeProvider = ({ children }) => {
  const [operatingMode, setOperatingMode] = useState(null);
  const [loading, setLoading] = useState(false); // Start as false since we don't fetch immediately
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  // Function to fetch operating mode - can be called manually after login
  const fetchOperatingMode = useCallback(async () => {
    if (hasAttemptedFetch) return; // Prevent multiple fetches
    
    // Check if user is authenticated
    const token = Cookies.get("token");
    const userAuth = Cookies.get("UserAuth");
    
    if (!token || !userAuth) {
      return;
    }

    setLoading(true);
    setHasAttemptedFetch(true);
    
    try {
      const response = await getOperatingMode();

      // The service now validates the response structure, so if we get here, it's valid
      if (response && response.data && response.data.status === true && response.data.data && response.data.data.mode) {
        setOperatingMode(response.data.data.mode);
      } else {
        // This shouldn't happen with the new service validation, but keep as safety net
        console.warn("Valid response received but missing mode data - using fallback");
        setOperatingMode('KANBAN');
      }
    } catch (error) {
      console.error('Error fetching operating mode:', error.message || error);
      // Use default fallback mode for any API failure (network, unauthorized, invalid response, etc.)
      setOperatingMode('KANBAN');
    } finally {
      setLoading(false);
    }
  }, [hasAttemptedFetch]);

  // Check for authentication changes and fetch operating mode when user logs in
  useEffect(() => {
    const checkAuthAndFetch = () => {
      const token = Cookies.get("token");
      const userAuth = Cookies.get("UserAuth");
      
      if (token && userAuth && !hasAttemptedFetch) {
        fetchOperatingMode();
      } else if (!token || !userAuth) {
        // User is not authenticated or has been logged out
        if (hasAttemptedFetch) {
          setOperatingMode(null);
          setLoading(false);
          setHasAttemptedFetch(false);
        }
      }
    };

    // Check immediately
    checkAuthAndFetch();

    // Set up an interval to check for authentication changes
    const authCheckInterval = setInterval(checkAuthAndFetch, 1000);

    return () => clearInterval(authCheckInterval);
  }, [fetchOperatingMode, hasAttemptedFetch]);

  // Use callbacks to ensure stable function references
  const isContinuousInventoryMode = useCallback(() => {
    return operatingMode === 'CONTINUOUS_INVENTORY';
  }, [operatingMode]);

  const isDeviceManagementMode = useCallback(() => {
    return operatingMode === 'DEVICE_MANAGEMENT';
  }, [operatingMode]);
  
  const isKanbanMode = useCallback(() => {
    return operatingMode === 'KANBAN';
  }, [operatingMode]);

  const updateOperatingMode = useCallback((mode) => {
    // Only update if it's actually changing to avoid unnecessary re-renders
    if (mode !== operatingMode) {
      setOperatingMode(mode);
    }
    setLoading(false);
  }, [operatingMode]);

  // Reset function for when user logs out
  const resetOperatingMode = useCallback(() => {
    setOperatingMode(null);
    setLoading(false);
    setHasAttemptedFetch(false);
  }, []);

  const contextValue = {
    operatingMode, 
    loading, 
    isContinuousInventoryMode,
    isDeviceManagementMode,
    isKanbanMode,
    updateOperatingMode,
    fetchOperatingMode,
    resetOperatingMode
  };

  return (
    <OperatingModeContext.Provider value={contextValue}>
      {children}
    </OperatingModeContext.Provider>
  );
};

export const useOperatingMode = () => {
  const context = useContext(OperatingModeContext);
  if (context === undefined) {
    throw new Error('useOperatingMode must be used within an OperatingModeProvider');
  }
  return context;
};

export default OperatingModeContext; 